import { useCallback, useMemo } from "react";
import { useProjectDetailContext } from "@/contexts/ProjectDetailContext";
import { auditLogApi, RecordAuditEventInput } from "@/services/auditLogApi";
import { AUDIT_ENTITY_PROMPT_LIBRARY, AuditAction, AuditStage } from "@/constants/audit";
import { buildSectionEntityNames } from "@/components/outline/sectionLabels";
import type { Section } from "@/components/outline/SortableRow";

/** Everything a call site still has to supply — `projectId` and `stage` are bound by the hook. */
export type AuditRecordInput = Omit<RecordAuditEventInput, "projectId" | "stage">;

/** A before/after snapshot, stored as a JSON object so the dialog's field diff can parse it. */
export type AuditSnapshot = Record<string, unknown> | null;

const serialiseSnapshot = (snapshot: AuditSnapshot): string | null =>
  snapshot === null ? null : JSON.stringify(snapshot);

/**
 * Returns a `record` for the given stage, with `projectId` taken from the surrounding
 * `ProjectDetailProvider`. Must therefore be called inside that provider.
 *
 * `record` returns `void`, not a Promise: `auditLogApi.recordAuditEvent` already swallows
 * its own errors so that a failed audit write can never break or delay the action it
 * describes, which leaves nothing for a call site to await or handle.
 */
export function useAuditRecorder(stage: AuditStage) {
  const { projectId } = useProjectDetailContext();

  return useCallback(
    (input: AuditRecordInput): void => {
      auditLogApi.recordAuditEvent({ ...input, projectId, stage });
    },
    [projectId, stage]
  );
}

/**
 * Section-scoped recorder: binds the entity type and resolves the section's display name,
 * so a call site supplies only the section id, the action and the two snapshots.
 *
 * Pass the sections in the order they are rendered — the numbering ("2.1") is derived
 * from that order, exactly as the outline and report views derive theirs.
 */
export function useSectionAuditRecorder(stage: AuditStage, sections: Section[]) {
  const record = useAuditRecorder(stage);
  const entityNames = useMemo(() => buildSectionEntityNames(sections), [sections]);

  return useCallback(
    (sectionId: string, action: AuditAction, before: AuditSnapshot, after: AuditSnapshot): void => {
      record({
        entityType: AUDIT_ENTITY_PROMPT_LIBRARY,
        entityId: sectionId,
        entityName: entityNames.get(sectionId) ?? null,
        action,
        before: serialiseSnapshot(before),
        after: serialiseSnapshot(after),
      });
    },
    [record, entityNames]
  );
}
